import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

const TIMEOUT_MS   = 30 * 60 * 1000; // 30 minutes
const CHECK_EVERY   = 15 * 1000;      // poll every 15s
const WRITE_THROTTLE = 5 * 1000;      // don't hammer localStorage on every mousemove
const STORAGE_KEY   = 'lastActivityAt';

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'wheel', 'scroll', 'touchstart'];

// Tracks the last-activity timestamp in localStorage (shared across tabs of
// the same origin), so a user active in one tab doesn't get logged out of
// an idle background tab, and vice versa — all tabs agree on one clock.
export const useInactivityLogout = () => {
  const { user } = useAuth();
  const lastWriteRef = useRef(0);

  useEffect(() => {
    if (!user) return;

    const markActivity = () => {
      const now = Date.now();
      if (now - lastWriteRef.current < WRITE_THROTTLE) return;
      lastWriteRef.current = now;
      localStorage.setItem(STORAGE_KEY, String(now));
    };

    markActivity();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, markActivity, { passive: true }));

    const interval = setInterval(() => {
      const last = parseInt(localStorage.getItem(STORAGE_KEY), 10) || Date.now();
      if (Date.now() - last >= TIMEOUT_MS) {
        window.dispatchEvent(new CustomEvent('session:expired', { detail: { reason: 'idle' } }));
      }
    }, CHECK_EVERY);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, markActivity));
      clearInterval(interval);
    };
  }, [user]);
};
